import { useState, useRef, useEffect } from "react";
import { Pencil, Plus, X } from "lucide-react";

interface MultiSelectToggleProps {
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  editable?: boolean;
}

export function MultiSelectToggle({ options, selected, onChange, editable = false }: MultiSelectToggleProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newValue, setNewValue] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const newInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  useEffect(() => {
    if (addingNew && newInputRef.current) {
      newInputRef.current.focus();
    }
  }, [addingNew]);

  const toggle = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((v) => v !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const startEdit = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setEditingIndex(index);
    setEditingValue(selected[index]);
  };

  const commitEdit = () => {
    if (editingIndex === null) return;
    const trimmed = editingValue.trim();
    if (trimmed) {
      const next = [...selected];
      next[editingIndex] = trimmed;
      onChange(next);
    }
    setEditingIndex(null);
    setEditingValue("");
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingValue("");
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  const commitNew = () => {
    const trimmed = newValue.trim();
    if (trimmed) {
      onChange([...selected, trimmed]);
    }
    setAddingNew(false);
    setNewValue("");
  };

  const cancelNew = () => {
    setAddingNew(false);
    setNewValue("");
  };

  const handleNewKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); commitNew(); }
    if (e.key === "Escape") { e.preventDefault(); cancelNew(); }
  };

  const removeSelected = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    onChange(selected.filter((_, i) => i !== index));
  };

  const isCustom = (value: string) => !options.includes(value);

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selIndex = selected.indexOf(option);
        const isSelected = selIndex !== -1;
        return (
          <button
            key={option}
            type="button"
            onClick={() => toggle(option)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              isSelected
                ? "bg-bauwerk-teal text-white border-bauwerk-teal"
                : "bg-white text-gray-700 border-gray-300 hover:border-bauwerk-teal hover:text-bauwerk-teal"
            }`}
          >
            {isSelected && editable ? (
              <span className="flex items-center gap-1">
                {editingIndex === selIndex ? (
                  <input
                    ref={editInputRef}
                    className="bg-transparent border-none outline-none text-white text-sm font-medium min-w-[60px] max-w-[200px] w-auto"
                    style={{ width: `${Math.max(editingValue.length, 4)}ch` }}
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={handleEditKeyDown}
                    onBlur={commitEdit}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span>{option}</span>
                )}
                {editingIndex !== selIndex && (
                  <Pencil
                    className="w-3 h-3 opacity-70 hover:opacity-100 flex-shrink-0"
                    onClick={(e) => startEdit(e, selIndex)}
                  />
                )}
              </span>
            ) : (
              option
            )}
          </button>
        );
      })}

      {editable && selected.filter(isCustom).map((value, i) => {
        const selIndex = selected.indexOf(value);
        return (
          <span
            key={`custom-${selIndex}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border bg-bauwerk-teal text-white border-bauwerk-teal"
          >
            {editingIndex === selIndex ? (
              <input
                ref={editInputRef}
                className="bg-transparent border-none outline-none text-white text-sm font-medium min-w-[60px] max-w-[200px] w-auto"
                style={{ width: `${Math.max(editingValue.length, 4)}ch` }}
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={handleEditKeyDown}
                onBlur={commitEdit}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span>{value}</span>
            )}
            {editingIndex !== selIndex && (
              <Pencil
                className="w-3 h-3 opacity-70 hover:opacity-100 cursor-pointer flex-shrink-0"
                onClick={(e) => startEdit(e, selIndex)}
              />
            )}
            <X
              className="w-3 h-3 opacity-70 hover:opacity-100 cursor-pointer flex-shrink-0"
              onClick={(e) => removeSelected(e, selIndex)}
            />
          </span>
        );
      })}

      {editable && (
        addingNew ? (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border border-bauwerk-teal bg-white text-bauwerk-teal">
            <input
              ref={newInputRef}
              className="bg-transparent border-none outline-none text-sm font-medium min-w-[80px] max-w-[200px]"
              style={{ width: `${Math.max(newValue.length, 6)}ch` }}
              placeholder="Eigener Eintrag"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={handleNewKeyDown}
              onBlur={commitNew}
            />
            <X
              className="w-3 h-3 opacity-70 hover:opacity-100 cursor-pointer flex-shrink-0"
              onMouseDown={(e) => { e.preventDefault(); cancelNew(); }}
            />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAddingNew(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-gray-400 text-gray-500 hover:border-bauwerk-teal hover:text-bauwerk-teal transition-colors"
          >
            <Plus className="w-3 h-3" />
            Eigener Eintrag
          </button>
        )
      )}
    </div>
  );
}
